import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { clsx } from 'clsx';
import { useUIStore } from '../store/uiStore';
import ProgressIndicator from './ProgressIndicator';
import ProgressBanner from './ProgressBanner';
import Alert from './Alert';
import Footer from './Footer';
import { useResponsiveIframe } from '../hooks/useResponsiveIframe';
import { useClaimStore } from '../store/claimStore';
import { useEmbeddedApp } from '../providers/EmbeddedAppProvider';
import { resolveSupportContact } from '../utils/supportContact';

interface LayoutProps {
  children: React.ReactNode;
  showProgress?: boolean;
  currentStep?: number;
  totalSteps?: number;
  completedSteps?: number[];
  className?: string;
  /**
   * Optional banner rendered above the main content. When provided, it takes precedence
   * over the ProgressBanner auto-instantiated via {@link showProgress}. Allows callers to
   * compose their own banner (e.g., contextual banner for an existing submitted claim).
   */
  banner?: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({
  children,
  showProgress = false,
  currentStep = 1,
  totalSteps = 21,
  completedSteps = [],
  className = '',
  banner
}) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { language, notifications, setLanguage, removeNotification } = useUIStore();
  const { currentScreenCode, getCompletedScreenCodes, getCurrentClaimType, setCurrentScreenCode } = useClaimStore();
  const [isEmbedded, setIsEmbedded] = useState(false);
  const viewport = useResponsiveIframe();
  const { tenantConfig } = useEmbeddedApp();

  /**
   * Whether the customer-service bar exists on this tenant at all (DEV-7932). Resolved ONCE here
   * because two unrelated-looking pieces of markup depend on the same answer — the `<Footer />`
   * itself and the bottom padding that reserves room for it. Reading the tenant flag twice (here
   * and in the footer's own guard) would let them drift; `utils/supportContact` stays the single
   * reader, so DEV-7934 changes the rule in one body.
   */
  const supportContact = resolveSupportContact(tenantConfig);

  // Automatically set screen code from URL path
  useEffect(() => {
    // Extract screen code from route like /claim/04M-00-REMBOURSEMENT
    const match = location.pathname.match(/\/claim\/([^/]+)/);
    if (match && match[1]) {
      const screenCode = match[1];
      setCurrentScreenCode(screenCode);
    }
  }, [location.pathname, setCurrentScreenCode]);

  // Détecter si l'app est dans un iframe/embedded
  useEffect(() => {
    // Vérifier si nous sommes dans un iframe
    const inIframe = window.self !== window.top;
    
    // Vérifier aussi si nous avons un parent PostMessage (plus fiable)
    const hasParentContext = window.parent !== window;
    
    // Vérifier les paramètres URL pour un mode embedded
    const params = new URLSearchParams(window.location.search);
    const embeddedParam = params.get('embedded') === 'true';

    setIsEmbedded(inIframe || hasParentContext || embeddedParam);
  }, []);

  const toggleLanguage = () => {
    const newLanguage = language === 'EN' ? 'FR' : 'EN';
    setLanguage(newLanguage);
  };

  return (
    <div className={clsx(
      'min-h-screen bg-gradient-to-br from-slate-50 to-white flex flex-col',
      isEmbedded && 'embedded-mode',
      viewport.isMobile && 'mobile-view',
      viewport.isTablet && 'tablet-view',
      viewport.isDesktop && 'desktop-view',
      viewport.orientation === 'landscape' && 'landscape-mode',
      className
    )}>
      {/* Header - Masqué en mode embedded */}
      {!isEmbedded && (
        <header className="bg-white border-b border-brand-gray-300 shadow-sm">
        <div className={clsx(
          'container-responsive py-3 xs:py-4',
          viewport.isMobile && 'px-4',
          viewport.isTablet && 'px-6',
          viewport.isDesktop && 'px-8'
        )}>
          <div className="flex items-center justify-end">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-1 xs:space-x-2 px-2 xs:px-3 py-2 rounded-md border border-brand-gray-300 hover:bg-tenant-50 hover:border-tenant-500 focus:outline-none focus:ring-2 focus:ring-tenant-500 focus:ring-offset-2 transition-all duration-200 touch-target"
              aria-label={`Switch to ${language === 'EN' ? 'French' : 'English'}`}
            >
              <Globe className="w-3 h-3 xs:w-4 xs:h-4 text-brand-gray-600" aria-hidden="true" />
              <span className="text-xs xs:text-sm font-medium text-tenant-800">
                {language === 'EN' ? 'FR' : 'EN'}
              </span>
            </button>
          </div>
        </div>
      </header>
      )}

      {/* Banner slot — explicit banner prop wins over the auto-instantiated ProgressBanner. */}
      {banner ? (
        banner
      ) : (
        showProgress && currentScreenCode && (
          <ProgressBanner
            currentScreenCode={currentScreenCode}
            claimType={getCurrentClaimType() || 'MEDICAL'}
            completedScreens={getCompletedScreenCodes()}
          />
        )
      )}

      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="relative z-50">
          <div className={clsx(
            'fixed space-y-2',
            viewport.isMobile ? 'top-2 left-2 right-2 max-w-full' : 'top-4 right-4 max-w-sm w-full'
          )}>
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className="animate-fade-in"
              >
                <Alert
                  type={notification.type}
                  title={notification.title}
                  message={notification.message}
                  dismissible={true}
                  onDismiss={() => removeNotification(notification.id)}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Content */}
      {/*
        `pb-40 sm:pb-32` is not spacing, it is clearance: the customer-service bar is
        `fixed bottom-0`, so without it the last rows of every screen sit under the bar. It is
        therefore conditional on the same answer as the bar (DEV-7932) — kept unconditional, a
        tenant that publishes no coordinates would get ~10rem of empty scroll at the bottom of
        all 21 screens, with nothing in it to explain the gap.
      */}
      <main className={clsx('flex-1 overflow-auto', supportContact && 'pb-40 sm:pb-32')}>
        <div className={clsx(
          'container-responsive',
          viewport.isMobile ? 'py-3 px-4' : 'py-4 xs:py-6',
          viewport.isTablet && 'px-6',
          viewport.isDesktop && 'px-8'
        )}>
          {children}
        </div>
      </main>

      {/* Footer - Masqué en mode embedded */}
      {!isEmbedded && (
        <footer className="bg-white border-t border-brand-gray-300 mt-auto">
        <div className="container-responsive py-4 xs:py-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-3 xs:space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-xs xs:text-sm text-brand-gray-600">
                © 2025 Canassistance. All rights reserved.
              </p>
            </div>
            
            <div className="flex flex-col xs:flex-row items-center space-y-2 xs:space-y-0 xs:space-x-4 md:space-x-6 text-xs xs:text-sm text-brand-gray-600">
              <a
                href="#"
                className="hover:text-tenant-800 transition-colors duration-200 touch-target text-center px-2 py-1"
                onClick={(e) => e.preventDefault()}
              >
                {t('steps.authorizations.termsAndConditions')}
              </a>
              <a
                href="#"
                className="hover:text-tenant-800 transition-colors duration-200 touch-target text-center px-2 py-1"
                onClick={(e) => e.preventDefault()}
              >
                {t('steps.authorizations.privacyPolicy')}
              </a>
              <a
                href="#"
                className="hover:text-tenant-800 transition-colors duration-200 touch-target text-center px-2 py-1"
                onClick={(e) => e.preventDefault()}
              >
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
      )}

      {/*
        Customer Service Footer — shown in both embedded and standalone mode, but only for a
        tenant allowed to publish the assistance line (DEV-7932). It was "always visible" until
        that story: the generic portal shell is shared by several corporate clients, and one
        insurer's travel-claims number printed there sends the others' members to a department
        that cannot open their file. The footer guards itself as well; this test is what removes
        it from the tree, and what keeps the `<main>` clearance above in agreement with it.
      */}
      {supportContact && <Footer />}
    </div>
  );
};

export default Layout;